import { useCallback, useMemo } from "react";
import { useReactFlow, useViewport } from "@xyflow/react";
import { useFeedbackStore } from "../../stores/feedback.js";

interface CanvasBackgroundProps {
  topologyType?: string;
}

/**
 * Custom canvas background layer rendered behind all nodes/edges.
 * Shows:
 * - Ambient radial glows behind running agents
 * - Subtle topology-type structural hints
 */
export function CanvasBackground({ topologyType }: CanvasBackgroundProps) {
  const { getNodes } = useReactFlow();
  const viewport = useViewport();
  const agentActivity = useFeedbackStore((s) => s.agentActivity);

  const nodes = getNodes();

  // Compute ambient glows for running agents
  const glows = useMemo(() => {
    return nodes
      .filter((n) => {
        const status = (n.data as any)?.status;
        return status === "running" || agentActivity.has(n.id);
      })
      .map((n) => {
        const status = (n.data as any)?.status;
        const backend = (n.data as any)?.backend ?? "";
        const isRunning = status === "running";

        // Pick glow color based on backend
        let color = "128, 0, 255"; // purple default
        if (backend.includes("openai")) color = "0, 220, 100";
        else if (backend.includes("cloudflare")) color = "255, 0, 128";
        else if (backend === "raw-api") color = "0, 200, 255";

        return {
          id: n.id,
          x: n.position.x + 120, // approximate center
          y: n.position.y + 50,
          color,
          opacity: isRunning ? 0.05 : 0.02,
          radius: isRunning ? 200 : 120,
        };
      });
  }, [nodes, agentActivity]);

  // Topology type structural hints
  const hints = useMemo(() => {
    if (!topologyType || nodes.length < 2) return null;

    const sorted = [...nodes].sort((a, b) => a.position.x - b.position.x);

    if (topologyType === "pipeline") {
      // Draw subtle flow arrows between node positions
      const points = sorted.map((n) => ({
        x: n.position.x + 120,
        y: n.position.y + 50,
      }));
      return { type: "pipeline" as const, points };
    }

    if (topologyType === "star") {
      // Draw radial lines from center
      const hub = nodes.find((n) => (n.data as any)?.isEntryPoint);
      if (!hub) return null;
      const center = { x: hub.position.x + 120, y: hub.position.y + 50 };
      const spokes = nodes
        .filter((n) => n.id !== hub.id)
        .map((n) => ({ x: n.position.x + 120, y: n.position.y + 50 }));
      return { type: "star" as const, center, spokes };
    }

    if (topologyType === "hierarchical") {
      // Draw subtle tree guide lines
      const root = nodes.find((n) => (n.data as any)?.isEntryPoint);
      if (!root) return null;
      const center = { x: root.position.x + 120, y: root.position.y + 80 };
      const children = nodes
        .filter((n) => n.id !== root.id)
        .map((n) => ({ x: n.position.x + 120, y: n.position.y }));
      return { type: "hierarchical" as const, center, children };
    }

    return null;
  }, [topologyType, nodes]);

  return (
    <svg
      className="absolute inset-0 w-full h-full pointer-events-none"
      style={{ zIndex: -1 }}
    >
      <defs>
        {glows.map((g) => (
          <radialGradient key={g.id} id={`glow-${g.id}`}>
            <stop offset="0%" stopColor={`rgba(${g.color}, ${g.opacity})`} />
            <stop offset="100%" stopColor={`rgba(${g.color}, 0)`} />
          </radialGradient>
        ))}
      </defs>

      {/* Ambient agent glows */}
      <g
        transform={`translate(${viewport.x}, ${viewport.y}) scale(${viewport.zoom})`}
      >
        {glows.map((g) => (
          <circle
            key={g.id}
            cx={g.x}
            cy={g.y}
            r={g.radius}
            fill={`url(#glow-${g.id})`}
          />
        ))}

        {/* Topology structural hints */}
        {hints?.type === "pipeline" && hints.points.length >= 2 && (
          <g opacity={0.06}>
            {hints.points.slice(0, -1).map((p, i) => {
              const next = hints.points[i + 1];
              return (
                <line
                  key={i}
                  x1={p.x}
                  y1={p.y}
                  x2={next.x}
                  y2={next.y}
                  stroke="#8000ff"
                  strokeWidth={1}
                  strokeDasharray="8 6"
                />
              );
            })}
            {/* Flow arrows */}
            {hints.points.slice(0, -1).map((p, i) => {
              const next = hints.points[i + 1];
              const mx = (p.x + next.x) / 2;
              const my = (p.y + next.y) / 2;
              const angle = Math.atan2(next.y - p.y, next.x - p.x);
              const ax = mx + Math.cos(angle) * 6;
              const ay = my + Math.sin(angle) * 6;
              const lx = mx + Math.cos(angle - 2.5) * 8;
              const ly = my + Math.sin(angle - 2.5) * 8;
              const rx = mx + Math.cos(angle + 2.5) * 8;
              const ry = my + Math.sin(angle + 2.5) * 8;
              return (
                <polygon
                  key={`arrow-${i}`}
                  points={`${ax},${ay} ${lx},${ly} ${rx},${ry}`}
                  fill="#8000ff"
                />
              );
            })}
          </g>
        )}

        {hints?.type === "star" && (
          <g opacity={0.04}>
            {hints.spokes.map((s, i) => (
              <line
                key={i}
                x1={hints.center.x}
                y1={hints.center.y}
                x2={s.x}
                y2={s.y}
                stroke="#8000ff"
                strokeWidth={1}
                strokeDasharray="4 8"
              />
            ))}
            <circle
              cx={hints.center.x}
              cy={hints.center.y}
              r={40}
              fill="none"
              stroke="#8000ff"
              strokeWidth={0.5}
              strokeDasharray="4 4"
            />
          </g>
        )}

        {hints?.type === "hierarchical" && (
          <g opacity={0.05}>
            {/* Vertical line from root down */}
            <line
              x1={hints.center.x}
              y1={hints.center.y}
              x2={hints.center.x}
              y2={hints.center.y + 60}
              stroke="#8000ff"
              strokeWidth={1}
              strokeDasharray="4 6"
            />
            {/* Horizontal connector */}
            {hints.children.length > 1 && (
              <line
                x1={Math.min(...hints.children.map((c) => c.x))}
                y1={hints.center.y + 60}
                x2={Math.max(...hints.children.map((c) => c.x))}
                y2={hints.center.y + 60}
                stroke="#8000ff"
                strokeWidth={1}
                strokeDasharray="4 6"
              />
            )}
            {/* Drop lines to children */}
            {hints.children.map((c, i) => (
              <line
                key={i}
                x1={c.x}
                y1={hints.center.y + 60}
                x2={c.x}
                y2={c.y}
                stroke="#8000ff"
                strokeWidth={1}
                strokeDasharray="4 6"
              />
            ))}
          </g>
        )}
      </g>
    </svg>
  );
}
